import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import apiClient from 'api/client';
import useRole from 'hooks/useRole';

export const biKeys = {
  keys: ['api-keys'],
  logs: ['query-logs'],
  stats: ['bi-stats'],
  usage: ['bi-usage'],
  ping: ['bi-ping'],
  connections: ['bi-connections'],
  connectionsAccess: ['bi-connections-access'],
};

// ── Connections (direct SQL data principals) ──────────────────────────────
export function useBiConnections({ pollNetwork = false, pollUsage = false } = {}) {
  const { isAdmin } = useRole();
  const pollingWindow = useRef(new Map());
  return useQuery({
    // A role downgrade must never reuse an administrator's shared rows.
    queryKey: [...biKeys.connections, isAdmin ? 'manage' : 'personal'],
    queryFn: () => apiClient.get('/api/v1/bi/connections/').then((r) => r.data),
    // Each pending revision gets a bounded three-minute observation window.
    // Successful fetches must not restart it indefinitely during an outage.
    refetchInterval: (query) => {
      if (!pollNetwork) {
        pollingWindow.current.clear();
        return pollUsage ? 60000 : false;
      }
      const pending = (query.state.data ?? [])
        .filter((row) => row.network_status?.state === 'pending'
          || (row.network_status?.state === 'applied' && row.network_status.revision !== row.network_revision))
        .map((row) => `${row.id}:${row.network_revision}`);
      for (const key of pollingWindow.current.keys()) {
        if (!pending.includes(key)) pollingWindow.current.delete(key);
      }
      const now = Date.now();
      for (const key of pending) {
        if (!pollingWindow.current.has(key)) pollingWindow.current.set(key, now + 180000);
      }
      // Continue observing lease freshness after an ACK; a stopped agent must
      // not leave a cached green status on screen indefinitely.
      return pending.some((key) => now < pollingWindow.current.get(key)) ? 5000 : 60000;
    },
  });
}

/** Conditional update: a conflict requires reloading before another save. */
export function useUpdateBiConnectionNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      apiClient.post(`/api/v1/bi/connections/${id}/network/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: biKeys.connections }),
  });
}

/** Whether direct SQL access is enabled for this workspace (staff-gated). */
export function useBiConnectionsAccess() {
  return useQuery({
    queryKey: biKeys.connectionsAccess,
    queryFn: () => apiClient.get('/api/v1/bi/connections/access/').then((r) => r.data),
  });
}

/**
 * Create a connection. The response carries `password` exactly once — hold it
 * in local dialog state only and call `.reset()` when the dialog closes.
 */
export function useCreateBiConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/api/v1/bi/connections/', data).then((r) => r.data),
    // Show-once: never let the password outlive its observers in the cache.
    gcTime: 0,
    onSuccess: () => qc.invalidateQueries({ queryKey: biKeys.connections }),
  });
}

/** Rotate the password (shared connections only). Same show-once handling. */
export function useRotateBiConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/api/v1/bi/connections/${id}/rotate/`).then((r) => r.data),
    gcTime: 0,
    onSuccess: () => qc.invalidateQueries({ queryKey: biKeys.connections }),
  });
}

/** Soft-revoke: access dies within ~3 minutes (measured; poll 60s +
 * Trino refresh 30s + auth cache); the row stays as history. */
export function useRevokeBiConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/bi/connections/${id}/`),
    onSuccess: (_response, id) => {
      qc.setQueriesData({ queryKey: biKeys.connections }, (rows) => rows?.filter((row) => row.id !== id));
      return qc.invalidateQueries({ queryKey: biKeys.connections });
    },
  });
}

// ── Stats (dashboard-wide) ─────────────────────────────────────────────────
export function useStats() {
  return useQuery({
    queryKey: biKeys.stats,
    queryFn: () => apiClient.get('/api/v1/bi/stats/').then((r) => r.data),
    refetchInterval: 30000,
  });
}

// ── BI usage analytics ─────────────────────────────────────────────────────
export function useBiUsage() {
  return useQuery({
    queryKey: biKeys.usage,
    queryFn: () => apiClient.get('/api/v1/bi/usage/').then((r) => r.data),
    refetchInterval: 30000,
  });
}

// ── API health / identity probe ────────────────────────────────────────────
export function useBiPing() {
  return useQuery({
    queryKey: biKeys.ping,
    queryFn: () => apiClient.get('/api/v1/bi/ping/').then((r) => r.data),
    refetchInterval: 30000,
    retry: false,
  });
}

// ── API Keys (CRUD) ────────────────────────────────────────────────────────
export function useApiKeys({ pollUsage = false } = {}) {
  return useQuery({
    queryKey: biKeys.keys,
    queryFn: () => apiClient.get('/api/v1/bi/api-keys/').then((r) => r.data),
    refetchInterval: pollUsage ? 60000 : false,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/api/v1/bi/api-keys/', data).then((r) => r.data),
    // Show-once: the raw key must not outlive its observers in the cache.
    gcTime: 0,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: biKeys.keys });
      qc.invalidateQueries({ queryKey: biKeys.usage });
    },
  });
}

export function useUpdateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      apiClient.patch(`/api/v1/bi/api-keys/${id}/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: biKeys.keys }),
  });
}

/**
 * Delete a key. Without `purge`, an active key is revoked (soft) and an
 * already-revoked key is removed. `purge: true` always hard-deletes.
 */
export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, purge = false }) =>
      apiClient.delete(`/api/v1/bi/api-keys/${id}/${purge ? '?purge=1' : ''}`),
    onSuccess: (_response, { id }) => {
      qc.setQueriesData({ queryKey: biKeys.keys }, (keys) => keys?.map((key) => key.id === id ? { ...key, is_active: false } : key));
      qc.invalidateQueries({ queryKey: biKeys.keys });
      qc.invalidateQueries({ queryKey: biKeys.usage });
    },
  });
}

// Back-compat alias — revoke a key.
export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/bi/api-keys/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: biKeys.keys }),
  });
}

// ── Query log ─────────────────────────────────────────────────────────────────
export function useQueryLogs() {
  return useQuery({
    queryKey: biKeys.logs,
    queryFn: () => apiClient.get('/api/v1/bi/query-logs/').then((r) => r.data),
    refetchInterval: 30000,
  });
}
